// Onboarding wizard: confirm the ZIP codes we hold and whether the client
// needs separate territories. ZIPs from /get-started are often wrong (header
// rows parsed as ZIPs, one "Default" territory, missing areas), and fixing
// them after launch costs a round of email.
//
// Changes are made on the existing ZIP Coverage page so they go through the
// same submission review as any other ZIP change; this step only reads ZIPs
// and records the client's answers in the onboarding payload.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import defaultClient from '@src/api/clients/default';

function summarize(rows) {
  const byTerritory = new Map();
  rows.forEach((r) => {
    const name = r.territory_name || 'No territory';
    if (!byTerritory.has(name)) {
      byTerritory.set(name, { name, zips: new Set(), verticals: new Set(), cities: new Map(), states: new Set() });
    }
    const t = byTerritory.get(name);
    t.zips.add(r.zip);
    if (r.vertical_name) t.verticals.add(r.vertical_name);
    if (r.state) t.states.add(r.state);
    if (r.city) t.cities.set(r.city, (t.cities.get(r.city) || 0) + 1);
  });
  return [...byTerritory.values()].map((t) => ({
    name: t.name,
    zipCount: t.zips.size,
    verticals: [...t.verticals].sort(),
    states: [...t.states].sort(),
    topCities: [...t.cities.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([c]) => c),
  }));
}

export default function StepServiceArea({ clientId, value, onChange }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!clientId) return;
    setRows(null);
    setError('');
    defaultClient.request({ url: `dashboard/client-zips/coverage_summary/?client_id=${clientId}` })
      .then((res) => setRows(Array.isArray(res?.data) ? res.data : []))
      .catch(() => { setRows([]); setError('We could not load your ZIP codes. Try Refresh, or email clients@leadgurus.com.'); });
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const territories = useMemo(() => summarize(rows || []), [rows]);
  const totalZips = useMemo(() => new Set((rows || []).map((r) => r.zip)).size, [rows]);
  const coverageUrl = `/zip-coverage?client_id=${clientId}`;
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Confirm Your Service Area</Typography>
        <Typography variant="body2" color="text.secondary">
          These are the ZIP codes we have for you. Please check they match where your team can actually
          serve. Our system tracks every ZIP and removes weak ones on its own, so there is no need to
          trim for performance.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {rows === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : totalZips === 0 ? (
        <Alert severity="warning">
          We don&apos;t have any ZIP codes for you yet. Add them on the ZIP Coverage page, then come back and
          press Refresh.
        </Alert>
      ) : (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {totalZips.toLocaleString()} ZIP codes in {territories.length} {territories.length === 1 ? 'territory' : 'territories'}
          </Typography>
          <Stack spacing={1.5}>
            {territories.map((t) => (
              <Box key={t.name}>
                <Typography variant="body2" fontWeight={600}>
                  {t.name} · {t.zipCount.toLocaleString()} ZIPs{t.states.length ? ` · ${t.states.join(', ')}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t.verticals.length ? `${t.verticals.join(', ')} · ` : ''}
                  {t.topCities.length ? `incl. ${t.topCities.join(', ')}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" endIcon={<OpenInNewIcon />} href={coverageUrl} target="_blank" rel="noopener">
          View map / make changes
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>

      <Box>
        <Typography variant="subtitle2" fontWeight={700}>Is this your service area?</Typography>
        <RadioGroup
          value={value.confirmed === true ? 'yes' : value.confirmed === false ? 'no' : ''}
          onChange={(e) => set({ confirmed: e.target.value === 'yes' })}
        >
          <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes, these ZIP codes are correct" disabled={totalZips === 0} />
          <FormControlLabel value="no" control={<Radio size="small" />} label="No, I need to make changes" />
        </RadioGroup>
        {value.confirmed === false && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Open <strong>View map / make changes</strong> to add or remove ZIPs, then press Refresh and
            confirm here. You can also describe the change below and our team will make it.
          </Alert>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={700}>Do you need separate territories?</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Separate territories get their own budgets and reporting. A territory is one market (for example
          Louisville and Lexington); we don&apos;t split a single market into city-by-city territories.
        </Typography>
        <RadioGroup
          value={value.territories || ''}
          onChange={(e) => set({ territories: e.target.value })}
        >
          <FormControlLabel value="single" control={<Radio size="small" />} label="No, one budget for my whole service area is fine" />
          <FormControlLabel value="as_shown" control={<Radio size="small" />} label="The territories shown above are right" disabled={territories.length < 2} />
          <FormControlLabel value="split" control={<Radio size="small" />} label="Yes, I need a different split" />
        </RadioGroup>
      </Box>

      {(value.territories === 'split' || value.confirmed === false) && (
        <TextField
          label="What should change?"
          placeholder="e.g. Split Louisville and Lexington with separate budgets; add Southern Indiana ZIPs"
          value={value.notes || ''}
          onChange={(e) => set({ notes: e.target.value })}
          multiline minRows={3} fullWidth
        />
      )}
    </Stack>
  );
}

// Done = ZIPs confirmed and a territory answer given. A pending change
// request with notes also counts, since our team takes it from there.
export function serviceAreaDone(value) {
  if (!value?.territories) return false;
  if (value.confirmed === true) return value.territories !== 'split' || !!value.notes;
  return value.confirmed === false && !!value.notes;
}
